import { useState, useRef } from "react";
import {
  CheckCircle2, AlertCircle, Loader2, Globe, Smartphone,
  CalendarCheck, ShoppingCart, UserCheck, CreditCard, Wrench,
  ChevronRight, ChevronLeft, Upload, X, Plus, Trash2,
  Palette, Image, FileText, Share2, MapPin, Clock, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// ── Types ─────────────────────────────────────────────────────────────────────

interface InspirationLink { url: string; notes: string; }

interface FormData {
  // Step 1 – You
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  fbProfileLink: string;
  // Step 2 – Business
  businessName: string;
  businessType: string;
  businessDescription: string;
  targetAudience: string;
  mainGoal: string;
  hasExistingWebsite: boolean;
  existingWebsiteUrl: string;
  // Step 3 – Inspiration & style
  inspirationLinks: InspirationLink[];
  brandStyle: string[];
  // Step 4 – Brand
  brandColourPrimary: string;
  brandColourSecondary: string;
  logoUrl: string;
  // Step 5 – Pages & features
  pagesNeeded: string[];
  features: string[];
  // Step 6 – Content & socials
  hasPhotos: string;
  hasContent: string;
  socialFacebook: string;
  socialInstagram: string;
  socialTiktok: string;
  socialOther: string;
  // Step 7 – Web address
  preferredDomain: string;
  // Step 8 – Final
  timeline: string;
  additionalInfo: string;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const BUSINESS_TYPES = [
  "Retail / Shop",
  "Food & Drink / Café / Restaurant / Takeaway",
  "Health & Beauty / Salon / Spa",
  "Trades / Construction / Home Services",
  "Fitness / Sports / Leisure",
  "Professional Services (Accountant, Solicitor, etc.)",
  "Childcare / Education / Tutoring",
  "Events / Entertainment",
  "Charity / Community Group / Not-for-Profit",
  "New Business Idea / Startup",
  "Other",
];

const MAIN_GOALS = [
  { id: "bookings", label: "Get more bookings / appointments" },
  { id: "showcase", label: "Show off my work / portfolio" },
  { id: "sell", label: "Sell products online" },
  { id: "info", label: "Give people information about my business" },
  { id: "leads", label: "Get more enquiries / leads" },
  { id: "community", label: "Build a community or membership" },
  { id: "other", label: "Something else" },
];

const BRAND_STYLES = [
  { id: "clean_modern", label: "Clean & modern", emoji: "✨", desc: "Simple, uncluttered, lots of white space" },
  { id: "bold_colourful", label: "Bold & colourful", emoji: "🎨", desc: "Eye-catching, vibrant, makes a statement" },
  { id: "luxury_premium", label: "Luxury & premium", emoji: "💎", desc: "High-end, sophisticated, elegant" },
  { id: "friendly_fun", label: "Friendly & fun", emoji: "😊", desc: "Warm, approachable, down to earth" },
  { id: "professional", label: "Professional & trustworthy", emoji: "🤝", desc: "Corporate, serious, confidence-inspiring" },
  { id: "traditional", label: "Traditional & classic", emoji: "🏛️", desc: "Timeless, established, heritage feel" },
];

const PAGES = [
  { id: "home", label: "Home page", desc: "Your front door — the first thing people see" },
  { id: "about", label: "About Us", desc: "Your story, your team, why you're the best" },
  { id: "services", label: "Services / What We Do", desc: "What you offer, explained clearly" },
  { id: "gallery", label: "Gallery / Portfolio", desc: "Photos of your work, products, or premises" },
  { id: "shop", label: "Online shop", desc: "Sell products directly from your website" },
  { id: "booking", label: "Book an appointment", desc: "Let people book online without calling you" },
  { id: "contact", label: "Contact page", desc: "How people get in touch with you" },
  { id: "testimonials", label: "Reviews & testimonials", desc: "What happy customers say about you" },
  { id: "faq", label: "FAQ", desc: "Answers to the questions you get asked all the time" },
  { id: "blog", label: "Blog / News", desc: "Share updates, tips, or news about your business" },
  { id: "menu", label: "Menu / Price list", desc: "Your food menu, treatment menu, or price guide" },
];

const FEATURES = [
  { id: "mobile_app", label: "A mobile app too", icon: Smartphone, desc: "An app people can download on their phone" },
  { id: "payments", label: "Take payments online", icon: CreditCard, desc: "Accept card payments through your site" },
  { id: "accounts", label: "Customer accounts", icon: UserCheck, desc: "Let customers log in and see their history" },
  { id: "ordering", label: "Online ordering", icon: ShoppingCart, desc: "Take food or product orders online" },
  { id: "bookings", label: "Online bookings", icon: CalendarCheck, desc: "Appointments booked automatically" },
  { id: "bespoke", label: "Something unique", icon: Wrench, desc: "A custom feature specific to your business" },
];

const STEPS = [
  "About You",
  "Your Business",
  "Inspiration & Style",
  "Your Brand",
  "Your Pages",
  "Photos & Socials",
  "Web Address",
  "Final Details",
];

// ── Reusable small bits ────────────────────────────────────────────────────────

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {message}
    </div>
  );
}

function SectionTitle({ emoji, title, sub }: { emoji: string; title: string; sub: string }) {
  return (
    <div className="text-center space-y-1.5 pb-2">
      <div className="text-3xl">{emoji}</div>
      <h2 className="text-xl font-bold text-slate-800">{title}</h2>
      <p className="text-slate-500 text-sm leading-relaxed max-w-xs mx-auto">{sub}</p>
    </div>
  );
}

function ToggleTile({
  selected, onClick, children,
}: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left flex items-start gap-3 p-3.5 rounded-xl border-2 transition-all ${
        selected ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
        selected ? "border-primary bg-primary" : "border-slate-300"
      }`}>
        {selected && <CheckCircle2 className="w-3 h-3 text-white fill-white" />}
      </div>
      {children}
    </button>
  );
}

// ── Logo uploader ──────────────────────────────────────────────────────────────

function LogoUpload({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { setError("File must be under 15MB"); return; }
    setError("");
    setUploading(true);

    try {
      const uuid = crypto.randomUUID();
      const r = await fetch(`/api/free-website/upload-asset/${uuid}`, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      const d = await r.json() as { url?: string; error?: string };
      if (!r.ok) { setError(d.error ?? "Upload failed"); return; }
      onChange(d.url!);
    } catch {
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="space-y-3">
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={e => void handleFile(e)} />

      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="Your logo" className="h-24 max-w-xs object-contain rounded-xl border border-slate-200 p-2 bg-white" />
          <button type="button" onClick={() => onChange("")}
            className="absolute -top-2 -right-2 bg-white rounded-full p-0.5 shadow border border-slate-200 text-slate-400 hover:text-red-500">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-60"
        >
          {uploading ? (
            <><Loader2 className="w-5 h-5 animate-spin text-primary mx-auto mb-1" /><p className="text-sm text-slate-500">Uploading…</p></>
          ) : (
            <><Upload className="w-6 h-6 text-slate-400 mx-auto mb-1" /><p className="text-sm font-medium text-slate-600">Click to upload your logo</p><p className="text-xs text-slate-400 mt-0.5">PNG, JPG or SVG — any size</p></>
          )}
        </button>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ── Steps ──────────────────────────────────────────────────────────────────────

type StepProps = { data: FormData; onChange: (p: Partial<FormData>) => void; error: string };

function Step1({ data, onChange, error }: StepProps) {
  return (
    <div className="space-y-5">
      <SectionTitle emoji="👋" title="Let's start with you"
        sub="A few details so we know who we're talking to. Ross will reach out personally once this is submitted." />
      <div className="space-y-4">
        <div className="space-y-1">
          <Label>Your full name <span className="text-red-500">*</span></Label>
          <Input value={data.contactName} onChange={e => onChange({ contactName: e.target.value })} placeholder="e.g. Jane Smith" />
        </div>
        <div className="space-y-1">
          <Label>Email address <span className="text-red-500">*</span></Label>
          <Input type="email" value={data.contactEmail} onChange={e => onChange({ contactEmail: e.target.value })} placeholder="you@example.com" />
        </div>
        <div className="space-y-1">
          <Label>Phone number <span className="text-red-500">*</span></Label>
          <Input type="tel" value={data.contactPhone} onChange={e => onChange({ contactPhone: e.target.value })} placeholder="e.g. 07700 900123" />
        </div>
        <div className="space-y-1">
          <Label>Facebook profile link <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Input value={data.fbProfileLink} onChange={e => onChange({ fbProfileLink: e.target.value })} placeholder="https://www.facebook.com/..." />
          <p className="text-xs text-slate-400">Paste your Facebook profile or page link so we can stay in touch.</p>
        </div>
      </div>
      {error && <ErrorBanner message={error} />}
    </div>
  );
}

function Step2({ data, onChange, error }: StepProps) {
  return (
    <div className="space-y-5">
      <SectionTitle emoji="🏪" title="Your business"
        sub="Tell us all about it. There are no wrong answers — just be yourself." />
      <div className="space-y-4">
        <div className="space-y-1">
          <Label>Business name <span className="text-red-500">*</span></Label>
          <Input value={data.businessName} onChange={e => onChange({ businessName: e.target.value })} placeholder='e.g. "The Ayr Bakehouse" or "My hair salon idea"' />
          <p className="text-xs text-slate-400">If you don't have a name yet, just put something close — we can figure it out together.</p>
        </div>
        <div className="space-y-1">
          <Label>What type of business is it? <span className="text-red-500">*</span></Label>
          <select value={data.businessType} onChange={e => onChange({ businessType: e.target.value })}
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/30">
            <option value="">— Pick the closest match —</option>
            {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <Label>Describe your business <span className="text-red-500">*</span></Label>
          <Textarea value={data.businessDescription} onChange={e => onChange({ businessDescription: e.target.value })}
            placeholder="What do you do? What do you sell or offer? What makes your business special?" rows={4} />
        </div>
        <div className="space-y-1">
          <Label>Who are your customers? <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Input value={data.targetAudience} onChange={e => onChange({ targetAudience: e.target.value })}
            placeholder='e.g. "Local families in Ayr" or "Small businesses in Ayrshire"' />
        </div>
        <div className="space-y-2">
          <Label>What's the main thing you want your website to do? <span className="text-slate-400 font-normal">(pick one)</span></Label>
          <div className="space-y-2">
            {MAIN_GOALS.map(g => (
              <ToggleTile key={g.id} selected={data.mainGoal === g.id} onClick={() => onChange({ mainGoal: data.mainGoal === g.id ? "" : g.id })}>
                <span className="text-sm font-medium text-slate-700">{g.label}</span>
              </ToggleTile>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={data.hasExistingWebsite} onChange={e => onChange({ hasExistingWebsite: e.target.checked })}
              className="accent-primary w-4 h-4" />
            <span className="text-sm text-slate-700">I already have a website</span>
          </label>
          {data.hasExistingWebsite && (
            <div className="space-y-1 pl-7">
              <Label>What's the address?</Label>
              <Input value={data.existingWebsiteUrl} onChange={e => onChange({ existingWebsiteUrl: e.target.value })}
                placeholder="https://www.yourbusiness.co.uk" />
            </div>
          )}
        </div>
      </div>
      {error && <ErrorBanner message={error} />}
    </div>
  );
}

function Step3({ data, onChange, error }: StepProps) {
  function setLink(i: number, patch: Partial<InspirationLink>) {
    const links = [...data.inspirationLinks];
    links[i] = { ...links[i]!, ...patch };
    onChange({ inspirationLinks: links });
  }
  function addLink() {
    if (data.inspirationLinks.length < 5)
      onChange({ inspirationLinks: [...data.inspirationLinks, { url: "", notes: "" }] });
  }
  function removeLink(i: number) {
    onChange({ inspirationLinks: data.inspirationLinks.filter((_, idx) => idx !== i) });
  }
  function toggleStyle(id: string) {
    onChange({
      brandStyle: data.brandStyle.includes(id)
        ? data.brandStyle.filter(s => s !== id)
        : [...data.brandStyle, id],
    });
  }

  return (
    <div className="space-y-6">
      <SectionTitle emoji="🔍" title="What catches your eye?"
        sub="Think about websites you've visited and thought 'I love this'. Share them with us — they don't have to be in your industry!" />

      <div className="space-y-3">
        <Label>Websites you like the look of <span className="text-slate-400 font-normal">(optional but really helpful)</span></Label>
        <p className="text-xs text-slate-500">
          Visit any website you love — even a big brand like Apple or Innocent Drinks — and paste the address here. Tell us what you like about it.
        </p>

        {data.inspirationLinks.length === 0 ? (
          <button type="button" onClick={addLink}
            className="w-full border-2 border-dashed border-slate-300 rounded-xl p-4 text-center text-sm text-slate-500 hover:border-primary hover:text-primary transition-colors">
            <Plus className="w-4 h-4 mx-auto mb-1" />
            Add a website I like
          </button>
        ) : (
          <div className="space-y-3">
            {data.inspirationLinks.map((link, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-4 space-y-2 border border-slate-200">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <Input value={link.url} onChange={e => setLink(i, { url: e.target.value })}
                    placeholder="https://www.example.com" className="bg-white" />
                  <button type="button" onClick={() => removeLink(i)} className="text-slate-400 hover:text-red-400 flex-shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <Input value={link.notes} onChange={e => setLink(i, { notes: e.target.value })}
                  placeholder='What do you like about it? e.g. "Love the colours" or "Simple and easy to use"'
                  className="bg-white text-sm" />
              </div>
            ))}
            {data.inspirationLinks.length < 5 && (
              <button type="button" onClick={addLink}
                className="flex items-center gap-1.5 text-sm text-primary hover:underline">
                <Plus className="w-3.5 h-3.5" /> Add another
              </button>
            )}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Label>What's the overall feel you're going for? <span className="text-slate-400 font-normal">(tick all that feel right)</span></Label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {BRAND_STYLES.map(s => {
            const selected = data.brandStyle.includes(s.id);
            return (
              <button key={s.id} type="button" onClick={() => toggleStyle(s.id)}
                className={`text-left p-3.5 rounded-xl border-2 transition-all ${
                  selected ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"
                }`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-lg">{s.emoji}</span>
                  <span className={`text-sm font-semibold ${selected ? "text-primary" : "text-slate-700"}`}>{s.label}</span>
                  {selected && <CheckCircle2 className="w-4 h-4 text-primary ml-auto" />}
                </div>
                <p className="text-xs text-slate-500">{s.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {error && <ErrorBanner message={error} />}
    </div>
  );
}

function Step4({ data, onChange, error }: StepProps) {
  return (
    <div className="space-y-6">
      <SectionTitle emoji="🎨" title="Your brand"
        sub="Don't worry if you haven't got any of this sorted yet — we can help you build it from scratch." />

      <div className="space-y-3">
        <Label className="flex items-center gap-1.5"><Image className="w-4 h-4" /> Your logo</Label>
        <p className="text-xs text-slate-500">
          If you have a logo, upload it here. Any format is fine — even a photo of a business card works!
          If you don't have one yet, no problem — we can design one together.
        </p>
        <LogoUpload value={data.logoUrl} onChange={url => onChange({ logoUrl: url })} />
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><Palette className="w-4 h-4" /> Your main colour</Label>
          <p className="text-xs text-slate-500">
            This is the colour most people associate with your brand — like the dominant colour on your social media or business cards.
            If you don't have one yet, just leave it and we'll chat about it.
          </p>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={data.brandColourPrimary || "#3b82f6"}
              onChange={e => onChange({ brandColourPrimary: e.target.value })}
              className="h-10 w-16 cursor-pointer rounded-lg border border-slate-200 p-0.5"
            />
            <Input
              value={data.brandColourPrimary}
              onChange={e => onChange({ brandColourPrimary: e.target.value })}
              placeholder="#3b82f6 or leave blank"
              className="flex-1 font-mono text-sm"
            />
            {data.brandColourPrimary && (
              <button type="button" onClick={() => onChange({ brandColourPrimary: "" })}
                className="text-slate-400 hover:text-red-400"><X className="w-4 h-4" /></button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Second colour <span className="text-slate-400 font-normal">(optional)</span></Label>
          <p className="text-xs text-slate-500">A second colour that complements your main one — like an accent or background colour.</p>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={data.brandColourSecondary || "#f59e0b"}
              onChange={e => onChange({ brandColourSecondary: e.target.value })}
              className="h-10 w-16 cursor-pointer rounded-lg border border-slate-200 p-0.5"
            />
            <Input
              value={data.brandColourSecondary}
              onChange={e => onChange({ brandColourSecondary: e.target.value })}
              placeholder="#f59e0b or leave blank"
              className="flex-1 font-mono text-sm"
            />
            {data.brandColourSecondary && (
              <button type="button" onClick={() => onChange({ brandColourSecondary: "" })}
                className="text-slate-400 hover:text-red-400"><X className="w-4 h-4" /></button>
            )}
          </div>
        </div>

        {(data.brandColourPrimary || data.brandColourSecondary) && (
          <div className="flex gap-2 pt-1">
            {data.brandColourPrimary && (
              <div className="flex-1 h-10 rounded-lg border border-slate-200 flex items-center justify-center text-xs font-medium"
                style={{ backgroundColor: data.brandColourPrimary, color: isLight(data.brandColourPrimary) ? "#333" : "#fff" }}>
                Main colour
              </div>
            )}
            {data.brandColourSecondary && (
              <div className="flex-1 h-10 rounded-lg border border-slate-200 flex items-center justify-center text-xs font-medium"
                style={{ backgroundColor: data.brandColourSecondary, color: isLight(data.brandColourSecondary) ? "#333" : "#fff" }}>
                Second colour
              </div>
            )}
          </div>
        )}
      </div>

      {error && <ErrorBanner message={error} />}
    </div>
  );
}

function isLight(hex: string) {
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 128;
  } catch { return true; }
}

function Step5({ data, onChange, error }: StepProps) {
  function togglePage(id: string) {
    onChange({ pagesNeeded: data.pagesNeeded.includes(id) ? data.pagesNeeded.filter(p => p !== id) : [...data.pagesNeeded, id] });
  }
  function toggleFeature(id: string) {
    onChange({ features: data.features.includes(id) ? data.features.filter(f => f !== id) : [...data.features, id] });
  }

  return (
    <div className="space-y-6">
      <SectionTitle emoji="📄" title="What goes on your website?"
        sub="Tick everything you'd like. Don't overthink it — we'll go through this together once we start." />

      <div className="space-y-3">
        <Label>Pages & sections <span className="text-slate-400 font-normal">(tick all that apply)</span></Label>
        <div className="grid grid-cols-1 gap-2">
          {PAGES.map(p => {
            const selected = data.pagesNeeded.includes(p.id);
            return (
              <ToggleTile key={p.id} selected={selected} onClick={() => togglePage(p.id)}>
                <div>
                  <p className="text-sm font-semibold text-slate-700">{p.label}</p>
                  <p className="text-xs text-slate-500">{p.desc}</p>
                </div>
              </ToggleTile>
            );
          })}
        </div>
      </div>

      <div className="space-y-3">
        <Label>Special features <span className="text-slate-400 font-normal">(tick if you need these)</span></Label>
        <div className="grid grid-cols-1 gap-2">
          {FEATURES.map(({ id, label, icon: Icon, desc }) => {
            const selected = data.features.includes(id);
            return (
              <ToggleTile key={id} selected={selected} onClick={() => toggleFeature(id)}>
                <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${selected ? "text-primary" : "text-slate-400"}`} />
                <div>
                  <p className="text-sm font-semibold text-slate-700">{label}</p>
                  <p className="text-xs text-slate-500">{desc}</p>
                </div>
              </ToggleTile>
            );
          })}
        </div>
      </div>

      {error && <ErrorBanner message={error} />}
    </div>
  );
}

function Step6({ data, onChange, error }: StepProps) {
  return (
    <div className="space-y-6">
      <SectionTitle emoji="📸" title="Photos & socials"
        sub="Great websites need great images. Don't worry if you don't have any yet — there are options!" />

      <div className="space-y-3">
        <Label className="flex items-center gap-1.5"><Image className="w-4 h-4" /> Do you have photos for your website?</Label>
        <div className="space-y-2">
          {[
            { id: "yes", label: "Yes — I'll send over my own photos", desc: "Great! We'll let you know what we need." },
            { id: "need_taken", label: "No — I'd like photos taken", desc: "We can arrange a professional shoot or give you tips on taking great phone photos." },
            { id: "stock_ok", label: "I'm happy to use stock photos", desc: "We can use high-quality free photos from the internet that suit your business." },
          ].map(opt => (
            <ToggleTile key={opt.id} selected={data.hasPhotos === opt.id} onClick={() => onChange({ hasPhotos: data.hasPhotos === opt.id ? "" : opt.id })}>
              <div>
                <p className="text-sm font-semibold text-slate-700">{opt.label}</p>
                <p className="text-xs text-slate-500">{opt.desc}</p>
              </div>
            </ToggleTile>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <Label className="flex items-center gap-1.5"><FileText className="w-4 h-4" /> Do you have the words written for your website?</Label>
        <div className="space-y-2">
          {[
            { id: "yes", label: "Yes — I'll write the text myself", desc: "Brilliant. We'll give you guidance on what to write for each page." },
            { id: "no_need_help", label: "No — I'd like help with the words", desc: "No problem at all. We can write it for you based on what you tell us." },
          ].map(opt => (
            <ToggleTile key={opt.id} selected={data.hasContent === opt.id} onClick={() => onChange({ hasContent: data.hasContent === opt.id ? "" : opt.id })}>
              <div>
                <p className="text-sm font-semibold text-slate-700">{opt.label}</p>
                <p className="text-xs text-slate-500">{opt.desc}</p>
              </div>
            </ToggleTile>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <Label className="flex items-center gap-1.5"><Share2 className="w-4 h-4" /> Your social media <span className="text-slate-400 font-normal">(optional)</span></Label>
        <p className="text-xs text-slate-500">If you have any social media pages, paste the links below. We'll add them to your website so people can follow you.</p>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs w-20 text-slate-500">Facebook</span>
            <Input value={data.socialFacebook} onChange={e => onChange({ socialFacebook: e.target.value })}
              placeholder="facebook.com/yourbusiness" className="text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs w-20 text-slate-500">Instagram</span>
            <Input value={data.socialInstagram} onChange={e => onChange({ socialInstagram: e.target.value })}
              placeholder="instagram.com/yourbusiness" className="text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs w-20 text-slate-500">TikTok</span>
            <Input value={data.socialTiktok} onChange={e => onChange({ socialTiktok: e.target.value })}
              placeholder="tiktok.com/@yourbusiness" className="text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs w-20 text-slate-500">Other</span>
            <Input value={data.socialOther} onChange={e => onChange({ socialOther: e.target.value })}
              placeholder="Any other link (YouTube, LinkedIn, etc.)" className="text-sm" />
          </div>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}
    </div>
  );
}

function Step7({ data, onChange, error }: StepProps) {
  return (
    <div className="space-y-5">
      <SectionTitle emoji="🌐" title="Your web address"
        sub="Every website needs its own unique address on the internet — like a phone number, but for your site." />

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-2xl p-4 space-y-2 text-sm text-blue-800 dark:text-blue-300">
        <p className="font-semibold">What's a web address?</p>
        <p className="leading-relaxed text-blue-700">
          A web address (also called a "domain") is what people type into their browser to find you — like{" "}
          <span className="font-mono bg-blue-100 px-1 rounded">yourbusiness.co.uk</span> or{" "}
          <span className="font-mono bg-blue-100 px-1 rounded">mybakery.com</span>.
          You rent it every year, a bit like a parking space for your website.
        </p>
        <p className="leading-relaxed text-blue-700">
          It typically costs around <strong>£15–£25 per year</strong>. We'll sort this out together — we just need to know if you already have one already.
        </p>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={data.hasExistingWebsite} onChange={e => onChange({ hasExistingWebsite: e.target.checked })}
            className="accent-primary w-4 h-4" />
          <span className="text-sm text-slate-700">I already have a web address (domain)</span>
        </label>
        {data.hasExistingWebsite && (
          <div className="space-y-1 pl-7">
            <Label>What is your existing web address?</Label>
            <Input value={data.existingWebsiteUrl} onChange={e => onChange({ existingWebsiteUrl: e.target.value })}
              placeholder="e.g. www.mybusiness.co.uk" />
          </div>
        )}
      </div>

      {!data.hasExistingWebsite && (
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5">
            <MapPin className="w-4 h-4" /> What would you like your web address to be?{" "}
            <span className="text-slate-400 font-normal">(optional)</span>
          </Label>
          <Input value={data.preferredDomain} onChange={e => onChange({ preferredDomain: e.target.value })}
            placeholder="e.g. mybakerytayrshire or ayrhairdresser" />
          <p className="text-xs text-slate-400">
            Just give us an idea — don't worry about the .co.uk or .com part for now. We'll check what's available together.
          </p>
        </div>
      )}

      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 dark:text-amber-300 space-y-1">
        <p className="font-semibold">Running costs — just so you know</p>
        <p className="text-amber-700 leading-relaxed">
          As well as the domain (~£15–25/year), keeping your site live online costs around{" "}
          <strong>£10–40 per month</strong> depending on the size of your project (hosting). We'll talk through all of this with you before anything is spent — no surprises.
        </p>
      </div>

      {error && <ErrorBanner message={error} />}
    </div>
  );
}

function Step8({ data, onChange, error }: StepProps) {
  return (
    <div className="space-y-5">
      <SectionTitle emoji="🚀" title="Almost there!"
        sub="Just a couple of final questions and then you're done." />

      <div className="space-y-3">
        <Label className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> Is there a deadline or any urgency?</Label>
        <div className="space-y-2">
          {[
            { id: "asap", label: "As soon as possible!" },
            { id: "1_month", label: "Within the next month" },
            { id: "3_months", label: "Within 3 months" },
            { id: "flexible", label: "No rush — whenever suits" },
          ].map(opt => (
            <ToggleTile key={opt.id} selected={data.timeline === opt.id} onClick={() => onChange({ timeline: data.timeline === opt.id ? "" : opt.id })}>
              <span className="text-sm text-slate-700">{opt.label}</span>
            </ToggleTile>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-1.5">
          <Sparkles className="w-4 h-4" /> Is there anything else you'd like us to know?{" "}
          <span className="text-slate-400 font-normal">(optional)</span>
        </Label>
        <Textarea value={data.additionalInfo} onChange={e => onChange({ additionalInfo: e.target.value })}
          placeholder="Anything at all — a specific idea you have, something you definitely want or definitely don't want, or anything that would help us understand your vision…"
          rows={4} />
      </div>

      <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-sm text-green-800 space-y-2">
        <p className="font-semibold">What happens next?</p>
        <p className="text-green-700 leading-relaxed">
          Once you submit this, Ross will review everything and get in touch to have a proper chat. This is the start of something exciting — we can't wait to help bring your vision to life. 💙
        </p>
      </div>

      {error && <ErrorBanner message={error} />}
    </div>
  );
}

// ── Progress bar ───────────────────────────────────────────────────────────────

function ProgressBar({ step, total }: { step: number; total: number }) {
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-xs text-slate-500">
        <span>Step {step + 1} of {total}</span>
        <span className="font-semibold text-primary">{STEPS[step]}</span>
      </div>
      <div className="flex gap-1">
        {Array.from({ length: total }, (_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
            i <= step ? "bg-primary" : "bg-slate-200"
          }`} />
        ))}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

const EMPTY: FormData = {
  contactName: "", contactEmail: "", contactPhone: "", fbProfileLink: "",
  businessName: "", businessType: "", businessDescription: "",
  targetAudience: "", mainGoal: "", hasExistingWebsite: false, existingWebsiteUrl: "",
  inspirationLinks: [], brandStyle: [],
  brandColourPrimary: "", brandColourSecondary: "", logoUrl: "",
  pagesNeeded: [], features: [],
  hasPhotos: "", hasContent: "",
  socialFacebook: "", socialInstagram: "", socialTiktok: "", socialOther: "",
  preferredDomain: "",
  timeline: "", additionalInfo: "",
};

export default function FreeWebsiteApply() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<FormData>(EMPTY);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const total = STEPS.length;

  function patch(p: Partial<FormData>) {
    setData(prev => ({ ...prev, ...p }));
    setError("");
  }

  function validate(): string {
    if (step === 0) {
      if (!data.contactName.trim()) return "Please enter your full name.";
      if (!data.contactEmail.includes("@")) return "Please enter a valid email address.";
      if (!data.contactPhone.trim()) return "Please enter your phone number.";
    }
    if (step === 1) {
      if (!data.businessName.trim()) return "Please enter your business name.";
      if (!data.businessType) return "Please pick the closest business type.";
      if (!data.businessDescription.trim()) return "Please describe your business — even a few sentences helps!";
    }
    return "";
  }

  function next() {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function back() {
    setError("");
    setStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function submit() {
    setSubmitting(true);
    setError("");
    try {
      const r = await fetch("/api/free-website/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          inspirationLinks: data.inspirationLinks.filter(l => l.url.trim()),
          existingWebsiteUrl: data.existingWebsiteUrl.trim() || null,
          fbProfileLink: data.fbProfileLink.trim() || null,
          brandColourPrimary: data.brandColourPrimary || null,
          brandColourSecondary: data.brandColourSecondary || null,
          logoUrl: data.logoUrl || null,
          preferredDomain: data.preferredDomain.trim() || null,
          additionalInfo: data.additionalInfo.trim() || null,
        }),
      });
      const d = await r.json() as { ok?: boolean; error?: string };
      if (!r.ok) { setError(d.error ?? "Something went wrong. Please try again."); return; }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-lg border border-slate-100 p-10 text-center space-y-5">
          <div className="text-5xl">🎉</div>
          <h1 className="text-2xl font-bold text-slate-800">You're all set!</h1>
          <p className="text-slate-600 leading-relaxed">
            Your brief has been submitted. Ross will have a good read through and be in touch with you soon to get started.
          </p>
          <p className="text-slate-500 text-sm">This is just the beginning — exciting things ahead! 💙</p>
        </div>
      </div>
    );
  }

  const stepProps: StepProps = { data, onChange: patch, error };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="bg-gradient-to-br from-primary to-blue-800 text-white text-center px-4 py-7">
        <p className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-1">Ayrshire Hub</p>
        <h1 className="text-2xl font-black">Your Free Website Brief</h1>
        <p className="text-white/75 text-sm mt-1">Help us understand your vision so we can build something amazing</p>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        <ProgressBar step={step} total={total} />

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          {step === 0 && <Step1 {...stepProps} />}
          {step === 1 && <Step2 {...stepProps} />}
          {step === 2 && <Step3 {...stepProps} />}
          {step === 3 && <Step4 {...stepProps} />}
          {step === 4 && <Step5 {...stepProps} />}
          {step === 5 && <Step6 {...stepProps} />}
          {step === 6 && <Step7 {...stepProps} />}
          {step === 7 && <Step8 {...stepProps} />}
        </div>

        <div className="flex gap-3">
          {step > 0 && (
            <Button variant="outline" onClick={back} className="flex-1 h-12 rounded-xl">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          {step < total - 1 ? (
            <Button onClick={next} className="flex-1 h-12 rounded-xl font-semibold">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={() => void submit()} className="flex-1 h-12 rounded-xl font-bold" disabled={submitting}>
              {submitting
                ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Submitting…</>
                : <>Send my brief 🚀</>}
            </Button>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 pb-4">
          You can go back and change anything at any time before submitting.
        </p>
      </div>
    </div>
  );
}
